import type { UseFormReturn } from 'react-hook-form';
import { getCdRates } from '@/app_data/widgets/RedVentures/getCdRatesRv';
import { getSavingsMma } from '@/app_data/widgets/RedVentures/getSavingsMmaRv';
import type { RatesData } from '@/app_data/widgets/RedVentures/utils';
import { defaultZipCode, formatDate } from '@/app_lib/utils/utils';

interface RatesWidgetMapperOutput {
  title: string;
  inputs: RatesInfoInputKey[];
  outputs: RatesInfoOutputKey[];
  getDataFunc: (params: RatesApiParameters) => Promise<RatesData[]>;
}

const months = 12;

// biome-ignore-start lint/style/useNamingConvention: <better naming>
export const RatesWidgetMapper: Record<RatesTypes, RatesWidgetMapperOutput> = {
  CD: {
    getDataFunc: async (params: RatesApiParameters) => {
      return await getCdRates(params);
    },
    inputs: ['zip', 'depositAmount', 'minTermLength', 'maxTermLength'],
    outputs: ['featured', 'apy', 'term', 'minDeposit', 'estEarnings'],
    title: `Best CD Rates for ${formatDate(new Date().toString())}`,
  },
  Savings: {
    getDataFunc: async (params: RatesApiParameters) => {
      return await getSavingsMma(params);
    },
    inputs: ['accountType', 'zip', 'depositAmount'],
    outputs: ['featured', 'apy', 'minBalance', 'estEarnings'],
    title: `The best savings account rates from our partners for ${formatDate(new Date().toString())}`,
  },
};
// biome-ignore-end lint/style/useNamingConvention: <>

// TODO: See below comments
export interface RatesApiParameters {
  accountType?: string | string[];
  depositAmount?: number; // temporarily undefined until strictNullCheck is true
  zip?: string | null; // temporarily undefined until strictNullCheck is true
  productTerm?: number[];
  page?: number;
  pageSize?: number;
  minTermLength?: number;
  maxTermLength?: number;
}

export enum RedVentureWidgets {
  SavingsMma = '5c6ba399-d32f-4e30-9722-8373c50873c3',
  CdRates = 'ed375ef3-c83b-43c1-8a5a-57347a4e9d42',
}

export type RatesTypes = 'CD' | 'Savings';

export const defaultRatesFormValues = {
  accountType: 'MMA,SAVINGS',
  depositAmount: 25000,
  maxTermLength: 36,
  minTermLength: 6,
  zip: defaultZipCode,
};

export type RatesInfoInputKey = 'zip' | 'depositAmount' | 'minTermLength' | 'maxTermLength' | 'accountType';

export type FormHookType = UseFormReturn<{
  zip: string;
  depositAmount: number;
  minTermLength: number;
  maxTermLength: number;
  accountType: string;
}>;

export const savingsAccountTypes = [
  { label: 'Savings & MMAs', value: 'MMA,SAVINGS' },
  { label: 'MMAs Only', value: 'MMA' },
  { label: 'Savings Only', value: 'SAVINGS' },
];

export const formatTerm = (term: number) => {
  if (term % months !== 0) {
    return `${term}mo`;
  }
  return `${Math.round(term / months)}yr`;
};

export interface RatesInfoOutputItemValue {
  tooltip?: string;
  title: string;
}

export interface RatesInfoOutputValue {
  value?: string;
  valueLabel?: string;
  date?: string;
  dateLabel?: string;
}

export type RatesInfoOutputKey = 'featured' | 'apy' | 'term' | 'minDeposit' | 'estEarnings' | 'minBalance';

export type RatesInfoOutput = Record<RatesInfoOutputKey, RatesInfoOutputValue>;
export type RatesInfoOutputItem = Record<RatesInfoOutputKey, RatesInfoOutputItemValue>;
export type RatesInfoOutputValueWithItem = Record<RatesInfoOutputKey, RatesInfoOutputValue & RatesInfoOutputItemValue>;

export const ratesInfoOutput: Record<RatesInfoOutputKey, RatesInfoOutputItemValue> = {
  apy: {
    title: 'APY',
    tooltip: 'APY = Annual Percentage Yield',
  },
  estEarnings: {
    title: 'EST. EARNINGS',
    tooltip:
      'Interest earned calculation based on a $$DEPOSIT_AMOUNT$ deposit amount held for a period of 1 year and is rounded to the nearest dollar. The estimation is provided by Bankrate and assumes no fees are accrued, the APY does not change, and minimum balance requirements are met. Actual earnings may vary.',
  },
  featured: {
    title: 'FEATURED OFFERS',
    tooltip:
      'Offers featured here have been sponsored by our partners, which impacts how, where, and in what order the products appear.',
  },
  minBalance: {
    title: 'MIN. BALANCE FOR APY',
  },
  minDeposit: {
    title: 'MIN. DEPOSIT',
  },
  term: {
    title: 'TERM',
  },
};

export const formatTermLength = (termLength: number): string => {
  const years = Math.round((termLength / months) * 10) / 10;
  if (years < 1) {
    return `${termLength} months`;
  }
  if (years === 1) {
    return `${years} year`;
  }
  if (years > 1 && years < 2) {
    return `${termLength} months`;
  }
  return `${years} years`;
};

// biome-ignore-start lint/nursery/noMagicNumbers: <these numbers are from redventures>
export const minTermLengths = [1, 6, 12, 18, 24, 36].map((term) => ({
  label: formatTermLength(term),
  value: term.toString(),
}));

export const maxTermLengths = (minTerm = 1) =>
  [1, 6, 12, 18, 24, 36, 48, 60, 72]
    .filter((term) => term >= minTerm)
    .map((term) => ({
      label: formatTermLength(term),
      value: term.toString(),
    }));
// biome-ignore-end lint/nursery/noMagicNumbers: <>
